import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import posthog from 'posthog-js';
import { captureReferralParams } from '@/lib/referralCapture';
import { useLanguage } from '@/contexts/LanguageContext';
// Event code box: valid codes and their days come from src/lib/promoCodes.js,
// which a test keeps equal to the server's list.
import EventCode from '@/components/landing/EventCode';
import founderImg from '@/assets/founder-2026.webp';
import chatImg from '@/assets/chat.webp';
import tapVideo from '@/assets/landing-tap-to-speak.mp4';
import tapToSpeakPoster from '@/assets/landing-tap-to-speak-poster.webp';
import timoSleepPoster from '@/assets/timo-home/timo-sleep.webp';
import timoSleepVideo from '@/assets/timo-home/goes to sleep.trimmed.mp4';
import sleepPoster from '@/assets/landing-timo-sleep-poster.webp';
import talkVideo from '@/assets/landing-talk-with-timo.mp4';
import talkPoster from '@/assets/landing-talk-with-timo-poster.webp';
import timoLanding from '@/assets/timolanding.webp';
import timoMark from '@/assets/timo-mark.webp';
import timoHandsUp from '@/assets/timohandsup.webp';
// Purpose-made cover for the lead article. The other four still borrow app art
// until the rest of the set is drawn.
import coverNotTalking from '@/assets/article-not-talking.webp';

/* ---------------------------------------------------------------------------
   Port of the LIVE keiki.app landing (not the prototype file), with Timo
   branding. Structure, geometry and type scale are taken from the shipped
   site: brand-coloured sticky header, brand hero, cloud edges cut from their
   decor.svg (the 14 overlapping circles below are the real coordinates),
   tinted cards, dark-on-brand stat band, article rail, pill FAQ rows, brand
   final CTA with the mascot breaking the top edge, brand footer.

   Palette swap (theirs -> ours):
     #6952E1 / #5B45CD brand -> #F2632C / #E2551D
     #FCFBFF page            -> #FFFBF7
     #1F2937 ink             -> #2B1A12
     #58B956 green CTA       -> white pill on brand, brand pill on light
   Lives at /landing-keiki. The shipped landing at / is untouched.
--------------------------------------------------------------------------- */

// Exact circle geometry from https://keiki.app/images/decor.svg (viewBox 1440x154).
const CLOUD = [
  [614.9, 213.8, 136.1], [800.4, 197.9, 109.6], [958.7, 206.3, 83.7],
  [638.5, 265.8, 139.1], [1430.4, 167, 107.3], [1088, 154.6, 90.1],
  [1255.2, 135, 135.4], [1181.4, 183.4, 104.2], [1312.7, 215.7, 93.7],
  [-64.9, 146.7, 122.5], [435.3, 193.8, 77.7], [280, 178.7, 124.4],
  [107.8, 156.5, 155.2], [260.3, 189.3, 111.2],
];

/** Scalloped edge between two sections. `from` = colour above, `to` = below. */
const Cloud = ({ from, to, flip = false }) => (
  <div
    aria-hidden="true"
    style={{ background: from, lineHeight: 0, transform: flip ? 'scaleY(-1)' : 'none' }}
  >
    <svg viewBox="0 0 1440 154" width="100%" style={{ display: 'block' }}>
      {CLOUD.map(([cx, cy, r], i) => (
        <circle key={i} cx={cx} cy={cy} r={r} fill={to} />
      ))}
    </svg>
  </div>
);

const ARTICLE_DIR = { en: 'articles', lt: 'straipsniai', ru: 'stati', de: 'artikel', no: 'artikler', es: 'articulos', fr: 'ressources' };
const ARTICLES = {
  en: [
    { slug: 'my-child-isnt-talking', title: "My child isn't talking: what to do" },
    { slug: 'nonverbal-child', title: 'Nonverbal child: how to help them communicate' },
    { slug: 'communicate-with-autistic-child', title: 'How to communicate with an autistic child' },
    { slug: 'child-social-anxiety', title: 'Child social anxiety: helping them speak up' },
    { slug: 'how-to-choose-aac-app', title: 'How to choose an AAC app for your child' },
  ],
  lt: [
    { slug: 'vaikas-nekalba', title: 'Vaikas nekalba: ką daryti?' },
    { slug: 'neverbalus-vaikas', title: 'Neverbalus vaikas: kaip padėti bendrauti' },
    { slug: 'kaip-bendrauti-su-autistisku-vaiku', title: 'Kaip bendrauti su autistišku vaiku' },
    { slug: 'vaiko-socialinis-nerimas', title: 'Vaiko socialinis nerimas: kaip padėti' },
    { slug: 'aac-programa-lietuviskai', title: 'AAK programa lietuviškai: kaip išsirinkti' },
  ],
  ru: [
    { slug: 'rebenok-ne-govorit', title: 'Ребёнок не говорит: что делать' },
    { slug: 'neverbalnyy-rebenok', title: 'Невербальный ребёнок: как помочь общаться' },
    { slug: 'obshchenie-s-rebenkom-s-autizmom', title: 'Как общаться с ребёнком с аутизмом' },
    { slug: 'socialnaya-trevozhnost-u-rebenka', title: 'Социальная тревожность у ребёнка' },
    { slug: 'kak-vybrat-prilozhenie-adk', title: 'Как выбрать приложение АДК' },
  ],
  de: [
    { slug: 'mein-kind-spricht-nicht', title: 'Mein Kind spricht nicht: was tun?' },
    { slug: 'nonverbales-kind', title: 'Nonverbales Kind: so hilfst du beim Sprechen' },
    { slug: 'kommunikation-mit-autistischem-kind', title: 'Kommunikation mit einem autistischen Kind' },
    { slug: 'soziale-angst-bei-kindern', title: 'Soziale Angst bei Kindern' },
    { slug: 'uk-app-fuer-kinder', title: 'Wie man eine UK-App auswählt' },
  ],
  no: [
    { slug: 'barnet-mitt-snakker-ikke', title: 'Barnet mitt snakker ikke: hva gjør jeg?' },
    { slug: 'ikke-verbalt-barn', title: 'Ikke-verbalt barn: slik hjelper du det' },
    { slug: 'kommunikasjon-med-autistisk-barn', title: 'Kommunikasjon med et autistisk barn' },
    { slug: 'sosial-angst-hos-barn', title: 'Sosial angst hos barn' },
    { slug: 'velge-ask-app', title: 'Hvordan velge en ASK-app' },
  ],
  es: [
    { slug: 'mi-hijo-no-habla', title: 'Mi hijo no habla: qué hacer' },
    { slug: 'nino-no-verbal', title: 'Niño no verbal: cómo ayudarle a comunicarse' },
    { slug: 'comunicarse-con-nino-autista', title: 'Cómo comunicarse con un niño autista' },
    { slug: 'ansiedad-social-en-ninos', title: 'Ansiedad social en niños: cómo ayudarle a hablar' },
    { slug: 'elegir-app-caa', title: 'Cómo elegir una app de CAA para tu hijo' },
  ],
  fr: [
    { slug: 'mon-enfant-ne-parle-pas', title: 'Mon enfant ne parle pas : que faire' },
    { slug: 'enfant-non-verbal', title: "Enfant non verbal : comment l'aider à communiquer" },
    { slug: 'communiquer-avec-enfant-autiste', title: 'Communiquer avec un enfant autiste' },
    { slug: 'anxiete-sociale-enfant', title: "Anxiété sociale chez l'enfant : l'aider à oser parler" },
    { slug: 'choisir-application-caa', title: 'Comment choisir une application de CAA pour son enfant' },
  ],
};

const LANGS = [
  { code: 'lt', label: 'Lietuvių' },
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },
  { code: 'es', label: 'Español' },
];

const FAQ_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((n) => ({
  q: `landing.faq${n}Question`,
  a: `landing.faq${n}Answer`,
}));

const LtFlag = () => (
  <svg viewBox="0 0 30 20" width="30" height="20" aria-label="Lietuva" role="img"
    style={{ borderRadius: 4, display: 'block', boxShadow: '0 2px 6px rgba(0,0,0,.18)' }}>
    <rect width="30" height="6.67" fill="#FDB913" />
    <rect y="6.67" width="30" height="6.66" fill="#006A44" />
    <rect y="13.33" width="30" height="6.67" fill="#C1272D" />
  </svg>
);

const KeikiStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');

    .kk {
      --brand:#F2632C; --brand-deep:#E2551D; --brand-dark:#C9491A;
      --ink:#2B1A12; --body:#6B554A; --muted:#9A8578;
      --page:#FFFBF7; --cream:#FFF4EC; --tint:#FFF1E9; --line:#F2E3D9; --accent:#FFC93C;
      font-family:'Nunito',system-ui,-apple-system,'Segoe UI',sans-serif;
      background:var(--page); color:var(--body); overflow-x:hidden;
      -webkit-font-smoothing:antialiased;
    }
    .kk *, .kk *::before, .kk *::after { box-sizing:border-box; }
    .kk h1, .kk h2, .kk h3, .kk h4 { color:var(--ink); margin:0; font-weight:900; letter-spacing:-.02em; }
    .kk p { margin:0; }
    .kk img { max-width:100%; display:block; }
    .kk-wrap { max-width:1200px; margin:0 auto; padding:0 20px; width:100%; }

    /* ---------- buttons ---------- */
    .kk-cta {
      display:inline-flex; align-items:center; justify-content:center; gap:8px;
      background:var(--brand); color:#fff; border:0; border-radius:14px;
      padding:16px 26px; font-family:inherit; font-size:17px; font-weight:800;
      text-decoration:none; cursor:pointer; white-space:nowrap;
      box-shadow:0 10px 24px rgba(242,99,44,.28);
      transition:transform .16s ease, background .16s ease;
    }
    .kk-cta:hover { background:var(--brand-deep); transform:translateY(-1px); }
    .kk-cta-sm { padding:11px 18px; font-size:15px; border-radius:12px; box-shadow:none; }
    /* on a brand background the CTA inverts, the way their green does on purple */
    .kk-cta-inv { background:#fff; color:var(--brand); box-shadow:0 10px 26px rgba(0,0,0,.14); }
    .kk-cta-inv:hover { background:#fff; color:var(--brand-deep); }
    .kk-ghost {
      background:transparent; border:0; font-family:inherit; font-size:15px; font-weight:800;
      color:inherit; cursor:pointer; text-decoration:none; white-space:nowrap; padding:10px 8px;
    }
    .kk-ghost:hover { opacity:.8; }

    /* ---------- header ---------- */
    /* The status bar is translucent in the native shell (index.html ships
       black-translucent + viewport-fit=cover), so without this the sticky brand
       bar starts at y=0 and the logo sits under the Dynamic Island. Padding the
       bar rather than the page keeps the strip behind the island brand-coloured
       and keeps the bar sticking correctly at top:0. Resolves to 0 in a normal
       browser tab, where nothing changes. */
    .kk-head { position:sticky; top:0; z-index:60; background:var(--brand-deep); color:#fff; padding-top:env(safe-area-inset-top, 0px); }
    .kk-head-in {
      max-width:1280px; margin:0 auto; height:64px; display:flex; align-items:center;
      gap:clamp(6px,1vw,18px); padding:0 clamp(14px,2vw,24px);
    }
    /* Mascot plus plain type. The mark is a transparent cut-out, so it needs no
       chip or plate behind it and sits straight on whatever the bar colour is. */
    .kk-logo {
      display:inline-flex; align-items:center; gap:9px; text-decoration:none; flex:0 0 auto;
      font-size:20px; font-weight:900; letter-spacing:-.02em; color:#fff;
      white-space:nowrap;
    }
    .kk-logo img { height:46px; width:auto; display:block; }
    .kk-nav { display:none; align-items:center; gap:2px; margin-left:6px; }
    .kk-nav-item { position:relative; }
    .kk-nav-link {
      background:transparent; border:0; cursor:pointer; font-family:inherit; color:#fff;
      font-size:clamp(12.5px,1.02vw,14.5px); font-weight:800; padding:9px clamp(3px,.48vw,9px);
      border-radius:10px; display:inline-flex; align-items:center; gap:4px; white-space:nowrap;
      text-decoration:none; opacity:.92;
      /* The nav only appears at 1000px and up, which includes iPad landscape,
         so these are touch targets. 35px tall was too small. */
      min-height:44px;
    }
    .kk-nav-link:hover, .kk-nav-open { opacity:1; background:rgba(255,255,255,.14); }
    .kk-caret { font-size:9px; opacity:.8; }
    .kk-head-right { display:none; align-items:center; gap:4px; margin-left:auto; flex:0 0 auto; }
    .kk-head-right .kk-ghost { color:#fff; font-size:clamp(12.5px,1.02vw,14.5px); padding:9px 8px; }
    .kk-head-right .kk-cta-sm { font-size:clamp(13px,1.05vw,15px); }
    .kk-mega, .kk-langmenu {
      position:absolute; top:calc(100% + 8px); left:0; z-index:70; background:#fff;
      border-radius:18px; box-shadow:0 20px 44px rgba(43,26,18,.18); padding:10px;
      display:flex; flex-direction:column; min-width:290px;
    }
    .kk-langmenu { left:auto; right:0; min-width:180px; }
    .kk-mega a, .kk-langmenu button {
      /* flex + min-height so each row is a 44px touch target on iPad, where
         this menu is reachable by finger */
      display:flex; align-items:center; min-height:44px;
      padding:10px 12px; border-radius:11px; text-decoration:none; text-align:left;
      font-family:inherit; font-size:14.5px; font-weight:700; color:var(--ink);
      background:transparent; border:0; cursor:pointer; width:100%;
    }
    .kk-mega a:hover, .kk-langmenu button:hover { background:var(--tint); color:var(--brand); }
    .kk-langmenu button[data-on="1"] { background:var(--tint); color:var(--brand); }
    .kk-burger {
      display:inline-flex; flex-direction:column; justify-content:center; align-items:center; gap:5px;
      margin-left:auto; width:44px; height:44px; background:transparent; border:0; cursor:pointer;
    }
    .kk-burger span { width:22px; height:2.5px; border-radius:2px; background:#fff; }
    .kk-drawer {
      position:fixed; inset:0; z-index:90; background:#fff; overflow-y:auto;
      /* Top: the drawer covers the whole screen including the status bar, so
         without the inset its logo and close button land under the notch or
         Dynamic Island. Bottom: the last drawer link used to sit under the
         iPhone home indicator. */
      padding:calc(16px + env(safe-area-inset-top, 0px)) 20px calc(40px + env(safe-area-inset-bottom, 0px));
    }
    .kk-drawer-top { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
    /* the drawer is white, so the lockup flips back to ink there */
    .kk-drawer .kk-logo { color:var(--ink); }
    .kk-drawer .kk-logo img { height:44px; }
    /* :not() so the orange CTA and the sign-in link at the bottom of the drawer
       keep their own styling. Plain ".kk-drawer a" outranked ".kk-cta" and was
       repainting the primary mobile CTA as a transparent text row. */
    .kk-drawer-row, .kk-drawer a:not(.kk-cta):not(.kk-ghost) {
      display:block; width:100%; text-align:left; background:transparent; border:0;
      border-bottom:1px solid var(--line); padding:17px 0; font-family:inherit; font-size:19px;
      font-weight:800; color:var(--ink); cursor:pointer; text-decoration:none;
    }
    .kk-drawer-langs { display:flex; flex-wrap:wrap; gap:8px; margin:20px 0; }
    .kk-drawer-langs button {
      border:1.5px solid var(--line); background:#fff; border-radius:999px; padding:9px 15px;
      font-family:inherit; font-size:14px; font-weight:800; color:var(--body); cursor:pointer;
      /* 34px tall pills in a phone-only drawer were under the 44px minimum */
      display:inline-flex; align-items:center; justify-content:center; min-height:44px;
    }
    .kk-drawer-langs button[data-on="1"] { background:var(--brand); border-color:var(--brand); color:#fff; }

    /* ---------- hero ---------- */
    .kk-hero { background:var(--brand); color:#fff; padding:44px 0 8px; }
    .kk-hero-in {
      max-width:1200px; margin:0 auto; padding:0 20px; display:grid; gap:24px; align-items:center;
    }
    /* .kk h1 sets ink for every heading, so the hero needs the extra class to win */
    .kk .kk-h1 { color:#fff; font-size:clamp(34px,5.4vw,80px); line-height:1.04; }
    .kk-hero p { color:rgba(255,255,255,.92); font-size:clamp(16px,1.5vw,20px); line-height:1.55; margin:18px 0 26px; max-width:620px; }
    .kk-hero-cta { display:flex; flex-wrap:wrap; gap:12px; align-items:center; }
    .kk-hero-code { min-height:44px; padding:10px 12px; color:#fff; text-decoration:underline; text-underline-offset:3px; }
    .kk-hero-img { width:100%; max-width:520px; margin:0 auto; }
    /* hero proof row: big value over a quiet label, three across */
    .kk-stats { display:flex; flex-wrap:wrap; gap:14px 34px; margin-top:28px; }
    .kk-stats > div { display:flex; flex-direction:column; gap:2px; }
    .kk-stats strong { font-size:clamp(20px,2.2vw,26px); font-weight:900; color:#fff; line-height:1.1; }
    .kk-stats span { font-size:13.5px; font-weight:700; color:rgba(255,255,255,.82); }
    /* radio / trust strip sits on the page colour under the cloud */
    .kk-trust { padding:26px 20px 6px; }
    .kk-trust-in {
      max-width:1200px; margin:0 auto; display:flex; flex-wrap:wrap; align-items:center;
      justify-content:center; gap:10px 28px; text-align:center;
    }
    /* the radio mention is the only outside proof on the page, so it reads as a
       real link rather than the grey micro-caption it used to be */
    .kk-radio {
      display:inline-flex; align-items:center; gap:10px; text-decoration:none;
      font-size:clamp(16px,1.5vw,19px); font-weight:800; color:var(--brand);
      /* 33px tall before; padded out to 44px without moving anything visually,
         since the strip below it has room to spare */
      padding:11px 2px; min-height:44px;
    }
    .kk-radio span { text-decoration:underline; text-underline-offset:3px; text-decoration-thickness:2px; }
    .kk-radio:hover span { color:var(--brand-deep); }

    /* ---------- sections ---------- */
    .kk-sec { padding:64px 0; }
    .kk-h2 { font-size:clamp(27px,3.6vw,56px); line-height:1.1; margin-bottom:14px; }
    .kk-center { text-align:center; }
    .kk-lede { font-size:clamp(15.5px,1.3vw,19px); line-height:1.6; max-width:660px; }
    .kk-lede-c { margin:0 auto 40px; text-align:center; }

    /* discover cards */
    .kk-cards { display:grid; gap:20px; margin-top:38px; }
    .kk-card { border-radius:28px; overflow:hidden; display:flex; flex-direction:column; }
    /* 4:3 rather than a short landscape strip: the clips are portrait, and a shallow
       window crops Timo down to a cut-off head. This is also the tap-to-speak clip's
       native ratio, so that one plays uncropped. */
    .kk-card-media { width:100%; aspect-ratio:4/3; height:auto; object-fit:cover; display:block; }
    .kk-card-body { padding:24px 24px 15px; flex:1; }
    .kk-card-body h3 { font-size:23px; margin-bottom:9px; }
    .kk-card-body p { font-size:15.5px; line-height:1.6; color:#5C4437; }
    /* the foot padding is trimmed by the same amount the link gains, so the card
       keeps its height while the link becomes a 44px tap target */
    .kk-card-foot { padding:0 24px 13px; }
    .kk-card-link {
      display:inline-flex; align-items:center; gap:6px; font-size:15.5px; font-weight:900;
      color:var(--brand); text-decoration:none; min-height:44px; padding:11px 0;
    }

    /* how it works */
    .kk-how { background:var(--cream); }
    .kk-how-in { max-width:1200px; margin:0 auto; padding:0 20px; display:grid; gap:30px; align-items:center; }
    .kk-steps { display:flex; flex-direction:column; gap:20px; margin:30px 0 32px; }
    .kk-step { display:flex; gap:16px; align-items:flex-start; }
    .kk-step-n {
      flex:0 0 auto; width:42px; height:42px; border-radius:14px; background:var(--brand); color:#fff;
      display:grid; place-items:center; font-size:18px; font-weight:900;
    }
    .kk-step h3 { font-size:19px; margin-bottom:5px; }
    .kk-step p { font-size:15.5px; line-height:1.55; }
    .kk-how-art { width:100%; max-width:420px; margin:0 auto; }

    /* inside grid */
    .kk-grid { display:grid; gap:18px; }
    .kk-tile {
      background:#fff; border:1.5px solid var(--line); border-radius:24px; padding:26px 24px 28px;
    }
    .kk-tile b {
      display:block; font-size:13px; font-weight:900; letter-spacing:.12em; color:var(--brand); margin-bottom:12px;
    }
    .kk-tile h3 { font-size:19px; margin-bottom:8px; }
    .kk-tile p { font-size:15px; line-height:1.6; }

    /* brand stat band */
    .kk-band { background:var(--brand); color:#fff; text-align:center; padding:10px 20px 64px; }
    .kk-band-in { max-width:900px; margin:0 auto; }
    .kk-band img { width:150px; margin:0 auto 18px; }
    .kk-band h2 { color:#fff; font-size:clamp(27px,3.6vw,54px); line-height:1.1; }
    .kk-band p { color:rgba(255,255,255,.9); font-size:17px; margin:16px 0 28px; }
    .kk-quotes { display:grid; gap:16px; margin:34px 0 30px; text-align:left; }
    .kk-quote { background:rgba(255,255,255,.14); border-radius:22px; padding:22px 24px; }
    .kk-quote p { color:#fff; font-size:16.5px; line-height:1.6; margin:0 0 12px; }
    .kk-quote b { display:block; font-size:15px; font-weight:900; color:#fff; }
    .kk-quote-who span span, .kk-quote > span { display:block; margin-top:2px; font-size:13.5px; font-weight:700; color:rgba(255,255,255,.78); }
    .kk-quote-who { display:flex; align-items:center; gap:12px; }
    .kk-quote-av {
      flex:0 0 auto; width:38px; height:38px; border-radius:50%; display:grid; place-items:center;
      background:rgba(255,255,255,.9); color:var(--brand-deep); font-size:16px; font-weight:900;
    }

    /* article rail */
    .kk-rail-head { display:flex; align-items:center; justify-content:space-between; gap:16px; margin-bottom:26px; }
    .kk-arrows { display:flex; gap:10px; }
    .kk-arrow {
      width:44px; height:44px; border-radius:999px; border:1.5px solid var(--line); background:#fff;
      font-size:19px; color:var(--ink); cursor:pointer; display:grid; place-items:center;
    }
    .kk-rail {
      display:grid; grid-auto-flow:column; grid-auto-columns:minmax(260px,300px); gap:20px;
      overflow-x:auto; scroll-snap-type:x mandatory; padding-bottom:8px;
      scrollbar-width:none; -ms-overflow-style:none;
    }
    .kk-rail::-webkit-scrollbar { display:none; }
    .kk-post { scroll-snap-align:start; text-decoration:none; display:block; }
    .kk-post img { width:100%; height:180px; object-fit:cover; border-radius:22px; margin-bottom:14px; }
    .kk-post b { font-size:12px; font-weight:900; letter-spacing:.12em; text-transform:uppercase; color:var(--brand); }
    .kk-post h3 { font-size:17.5px; line-height:1.35; margin:8px 0 8px; }
    .kk-post span { font-size:13.5px; color:var(--muted); font-weight:700; }

    /* founder / long form */
    .kk-seo { background:var(--cream); }
    .kk-seo-in { max-width:1040px; margin:0 auto; padding:0 20px; display:grid; gap:26px; align-items:center; }
    .kk-seo-art img { width:100%; max-width:300px; border-radius:26px; margin:0 auto; display:block; }
    .kk-seo-body p { font-size:16.5px; line-height:1.7; margin:0 0 14px; }
    /* the pull quote carries the story, so it reads a size up from the body copy */
    .kk-pull { font-size:clamp(19px,2.1vw,23px); line-height:1.45; font-weight:800; color:var(--ink); }
    .kk-sign { margin-top:18px; }
    .kk-sign b { display:block; font-size:16px; font-weight:900; color:var(--ink); }
    .kk-sign span { display:block; margin-top:2px; font-size:14px; font-weight:700; color:var(--muted); }

    /* faq */
    /* long-form search copy: collapsed to a taster, opens on demand. The text is
       always in the DOM (crawlers read it either way), only its height is cut. */
    .kk-long h2 { margin-bottom:20px; }
    /* capped so the paragraphs do not run 100+ characters wide on an iPad in
       portrait, where this block is still a single column */
    .kk-long-body { position:relative; max-width:68ch; max-height:190px; overflow:hidden; transition:max-height .32s ease; }
    /* 1600px was not enough once the copy is translated: German and Russian run
       around 1.4x English, so the last paragraph was cut off with no way to reach
       it. The open state is now taller than any translation can be. */
    .kk-long-body[data-open='1'] { max-height:4000px; }
    .kk-long-body p { margin:0 0 14px; font-size:16px; line-height:1.72; color:var(--body); }
    .kk-long-body::after {
      content:''; position:absolute; left:0; right:0; bottom:0; height:96px; pointer-events:none;
      background:linear-gradient(to bottom, rgba(255,251,247,0), var(--page));
    }
    .kk-long-body[data-open='1']::after { opacity:0; }
    /* was a ~22px tall text link; padded to 44px with the top margin trimmed by
       the same amount so the spacing on screen is unchanged */
    .kk-long-toggle {
      margin-top:7px; background:transparent; border:0; padding:11px 0; cursor:pointer; font-family:inherit;
      min-height:44px; display:inline-flex; align-items:center;
      font-size:16px; font-weight:900; color:var(--brand); text-decoration:underline; text-underline-offset:4px;
    }

    .kk-faq { display:flex; flex-direction:column; gap:12px; max-width:840px; margin:0 auto; }
    .kk-faq-item { background:var(--tint); border-radius:20px; overflow:hidden; }
    .kk-faq-item[data-open="1"] { background:#fff; box-shadow:0 12px 30px rgba(43,26,18,.08); }
    .kk-faq-q {
      width:100%; display:flex; align-items:center; justify-content:space-between; gap:16px;
      background:transparent; border:0; cursor:pointer; padding:20px 22px; text-align:left;
      font-family:inherit; font-size:17px; font-weight:800; color:var(--ink);
    }
    .kk-faq-sign {
      flex:0 0 auto; width:30px; height:30px; border-radius:999px; background:var(--brand); color:#fff;
      display:grid; place-items:center; font-size:18px; font-weight:900;
    }
    .kk-faq-a { padding:0 22px 22px; font-size:15.5px; line-height:1.65; }

    /* final cta */
    .kk-final { background:var(--brand); color:#fff; text-align:center; padding:0 20px 76px; position:relative; }
    .kk-final-art { width:190px; margin:-84px auto 0; position:relative; z-index:2; }
    /* this section has no inner wrapper, so on a wide desktop the headline and
       the price line used to run the full width of the window */
    .kk-final h2 { color:#fff; font-size:clamp(28px,3.8vw,58px); line-height:1.08; margin:18px auto 0; max-width:16ch; }
    .kk-final p { color:rgba(255,255,255,.92); font-size:17px; margin:16px auto 28px; max-width:52ch; }
    .kk-final small { display:block; margin:18px auto 0; max-width:52ch; font-size:14.5px; color:rgba(255,255,255,.85); }

    /* event-code box, sitting quietly under the final CTA on the brand block */
    /* the event code box styles itself: src/components/landing/EventCode.jsx */

    /* promo */
    .kk-promo { background:var(--page); padding:44px 20px; text-align:center; }
    .kk-promo p { font-size:14.5px; font-weight:800; color:var(--muted); margin-bottom:12px; }
    .kk-promo form { display:flex; gap:8px; max-width:340px; margin:0 auto; }
    .kk-promo input {
      flex:1; border:1.5px solid var(--line); border-radius:12px; padding:12px 14px;
      font-family:inherit; font-size:15px; background:#fff; color:var(--ink);
    }
    .kk-promo small { display:block; margin-top:10px; font-size:11.5px; color:var(--muted); }

    /* footer */
    .kk-foot { background:var(--brand-deep); color:rgba(255,255,255,.9); padding:52px 0 0; }
    .kk-foot-in { max-width:1200px; margin:0 auto; padding:0 20px; display:grid; gap:34px; }
    .kk-foot h4 { color:#fff; font-size:12.5px; letter-spacing:.12em; text-transform:uppercase; margin-bottom:14px; }
    /* the list gap moved into the links themselves: same rhythm on screen, but
       each row is now a 44px tap target instead of a 20px one */
    .kk-foot ul { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:0; }
    .kk-foot a, .kk-foot-row {
      color:#fff; text-decoration:none; font-size:15px; font-weight:700; background:transparent;
      border:0; padding:0; text-align:left; font-family:inherit; cursor:pointer;
    }
    .kk-foot ul a, .kk-foot-row {
      display:flex; align-items:center; min-height:44px; padding:11px 0;
    }
    .kk-foot a:hover, .kk-foot-row:hover { text-decoration:underline; }
    /* the footer has room, so the mark runs bigger there than in the bar */
    .kk-foot .kk-logo { margin-bottom:14px; color:#fff; font-size:22px; }
    .kk-foot .kk-logo img { height:52px; }
    .kk-foot-blurb { font-size:15px; line-height:1.6; color:rgba(255,255,255,.85); max-width:320px; }
    .kk-foot-social { display:flex; gap:6px; margin-top:10px; }
    /* text links, so they needed height and width padding to be tappable */
    .kk-foot-social a { display:inline-flex; align-items:center; min-height:44px; padding:0 10px; }
    .kk-foot-social a:first-child { padding-left:0; }
    .kk-foot-bottom {
      max-width:1200px; margin:38px auto 0; padding:20px; border-top:1px solid rgba(255,255,255,.2);
      font-size:13.5px; color:rgba(255,255,255,.8);
    }

    /* sticky mobile cta */
    .kk-sticky {
      position:fixed; left:0; right:0; bottom:0; z-index:70; padding:10px 16px calc(10px + env(safe-area-inset-bottom));
      background:rgba(255,251,247,.94); backdrop-filter:blur(10px); border-top:1px solid var(--line);
    }
    .kk-sticky .kk-cta { width:100%; }
    /* the bar is ~74px plus the home indicator, and the flat 78px used to let the
       copyright line hide behind it on an iPhone */
    .kk { padding-bottom:calc(86px + env(safe-area-inset-bottom, 0px)); }

    /* ---------- tablet ---------- */
    @media (min-width:640px){
      .kk-cards, .kk-grid, .kk-quotes { grid-template-columns:repeat(2,1fr); }
      .kk-foot-in { grid-template-columns:repeat(2,1fr); }
    }

    /* ---------- desktop ---------- */
    @media (min-width:1000px){
      .kk { padding-bottom:0; }
      .kk-sticky { display:none; }
      .kk-nav, .kk-head-right { display:flex; }
      .kk-burger { display:none; }
      .kk-head-in { height:72px; }
      /* 1000-1259: shed the optional nav items, the sign-in link and the
         language word so the CTA never gets pushed past the viewport edge
         (the header clips silently, so overflow here is invisible damage). */
      @media (max-width:1259px){
        .kk-nav-item[data-opt="1"] { display:none; }
        .kk-head-right .kk-ghost { display:none; }
        .kk-lang-label { display:none; }
      }
      .kk-hero { padding:72px 0 20px; }
      /* Timo left, words right. The copy still comes first in the DOM so the
         phone stack leads with the headline instead of the picture. */
      .kk-hero-in { grid-template-columns:.95fr 1.05fr; gap:40px; }
      .kk-hero-copy { order:2; }
      .kk-hero-img { order:1; max-width:100%; }
      .kk-sec { padding:88px 0; }
      /* Three tiles, three columns. A four-column track left an empty cell and
         dragged the whole row off-centre under the centred heading. */
      .kk-cards, .kk-grid { grid-template-columns:repeat(3,1fr); }
      .kk-how-in { grid-template-columns:1.05fr .95fr; gap:56px; }
      .kk-how-art { max-width:100%; }
      .kk-band { padding:20px 20px 96px; }
      .kk-quotes { grid-template-columns:repeat(2,1fr); }
      /* portrait beside the story, Keiki's two-up text block */
      .kk-seo-in { grid-template-columns:340px 1fr; gap:56px; }
      /* two text columns, the way the long block reads on the reference site */
      /* two columns from here, so the single-column cap above is released */
      .kk-long-body { columns:2; column-gap:56px; max-width:none; max-height:250px; }
      /* headroom for the longer translations, same reason as the mobile rule */
      .kk-long-body[data-open='1'] { max-height:2000px; }
      .kk-seo-art img { max-width:340px; }
      .kk-final { padding:0 20px 104px; }
      .kk-final-art { width:230px; margin-top:-110px; }
      .kk-foot-in { grid-template-columns:1.6fr 1fr 1.3fr 1fr; gap:40px; }
    }
  `}</style>
);

const LandingKeiki = () => {
  const { language, setLanguage, t } = useLanguage();

  const [drawer, setDrawer] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);
  const [longOpen, setLongOpen] = useState(false);
  const railRef = useRef(null);
  const [railStart, setRailStart] = useState(true);
  const [railEnd, setRailEnd] = useState(false);
  // The card clips loop forever, so they fall back to their poster frame when the
  // visitor's OS asks for reduced motion.
  const [stillCards, setStillCards] = useState(false);
  // The Articles menu in the header used to open on hover only. The header nav is
  // visible from 1000px up, which includes iPad landscape, where mouseenter fires
  // with the tap and mouseleave never fires, so the menu was unusable there and
  // for anyone on a keyboard. Hover is now only wired up on real pointers.
  const [canHover, setCanHover] = useState(true);

  // Referral / affiliate links point at this page (server/referral-helper.js
  // and server/affiliate-helper.js both build `${origin}/?ref=` and
  // `${origin}/?affiliate=`), so the code has to be taken off the URL before
  // the visitor navigates away. Login.jsx and AuthCallback.jsx read it back out
  // after signup. Runs first, before any of the presentation effects.
  useEffect(() => {
    captureReferralParams();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setStillCards(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const apply = () => setCanHover(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  // Both header menus used to close on mouseleave only. On an iPad, where the
  // desktop nav is visible but mouseleave never fires, an opened menu stayed
  // open until you picked something. Tapping elsewhere or pressing Escape now
  // closes them.
  useEffect(() => {
    if (!megaOpen && !langOpen) return undefined;
    const closeAll = () => { setMegaOpen(false); setLangOpen(false); };
    const onPointerDown = (e) => {
      if (e.target instanceof Element && e.target.closest('.kk-nav-item')) return;
      closeAll();
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') closeAll(); };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [megaOpen, langOpen]);

  // Straight into the funnel: /onboarding -> /scratch -> /pricing. There is one
  // onboarding now (pages/FbOnboarding, the same one paid traffic gets) and it
  // is public, so the old detour through /login is gone. That funnel asks for
  // the account itself near the end, after the visitor has answered a few
  // questions about their child, instead of demanding a signup from a stranger
  // who has not seen anything yet. Logged-in visitors never reach this button:
  // "/" sits behind PublicEntryRoute, which routes them on before the landing
  // renders.
  const ctaHref = '/onboarding';
  // dest defaults to the funnel, but the radio link goes to YouTube, so it says
  // so rather than reporting a destination it never sends anyone to.
  const fireCta = (cta, dest = ctaHref) => {
    try {
      posthog?.capture('landing_cta_clicked', { cta, dest, src: 'landing_keiki', language });
    } catch {}
  };

  // "Turiu kodą" in the hero: bring the code box at the foot of the page into
  // view and put the cursor in it. A jump on tap, not a reveal on scroll.
  const jumpToCode = () => {
    fireCta('code', '#kk-code');
    const box = document.getElementById('kk-code');
    if (!box) return;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
      || document.body.classList.contains('reduce-motion');
    box.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });
    box.querySelector('input')?.focus({ preventScroll: true });
  };

  const dir = ARTICLE_DIR[language] || ARTICLE_DIR.en;
  const posts = ARTICLES[language] || ARTICLES.en;
  const covers = [coverNotTalking, chatImg, timoSleepPoster, timoLanding, timoHandsUp];
  // All five articles shipped together on 2026-07-01 (see git history), so the
  // byline carries one real date rather than five invented ones.
  const postDate = new Date(2026, 6, 1).toLocaleDateString(language === 'lt' ? 'lt-LT' : language, {
    year: 'numeric', month: 'short',
  });

  // Section headings carry full stops in several locales; nav chips read better without them.
  const navLabel = (key) => String(t(key) || '').replace(/[.:]\s*$/, '');
  // opt:1 = droppable on narrow desktops. Our section names run 2-3 words in
  // every locale, so a full seven-item bar only fits from ~1180px up; below
  // that these two fold away instead of pushing the CTA off the screen edge.
  const sections = [
    { id: 'how', label: navLabel('landing.howItWorks') },
    { id: 'inside', label: navLabel('landing.whatWeBuilt') },
    { id: 'loved', label: navLabel('landing.parentsLoveIt'), opt: 1 },
    { id: 'story', label: navLabel('landing.ourStory'), opt: 1 },
    { id: 'faq', label: navLabel('landing.faqSectionTitle') },
  ];

  const goTo = (id) => {
    setDrawer(false);
    setTimeout(() => {
      // Smooth scrolling is motion, so it follows the same preference as the
      // card clips: jump straight there when the user has asked for less motion.
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';
      document.getElementById(id)?.scrollIntoView({ behavior, block: 'start' });
    }, 60);
  };

  useEffect(() => {
    document.body.style.overflow = drawer ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawer]);

  const syncArrows = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setRailStart(el.scrollLeft <= 4);
    setRailEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }, []);

  useEffect(() => { syncArrows(); }, [syncArrows, language]);

  const glide = (delta) => {
    const el = railRef.current;
    if (!el) return;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';
    el.scrollBy({ left: delta * 320, behavior });
    setTimeout(syncArrows, 380);
  };

  const cards = [
    {
      bg: '#FFF3C9', video: tapVideo, media: tapToSpeakPoster,
      title: t('landing.card1Title'), body: t('landing.modeNonverbalDesc'),
    },
    // These two were shot portrait, so focus picks the band of the frame the 4:3
    // window keeps: head down to hands for the talking clip, the bed for the other.
    {
      bg: '#FFE0CE', video: timoSleepVideo, media: sleepPoster, focus: '50% 48%',
      title: t('landing.card3Title'), body: t('landing.modeLiveDesc'),
    },
    {
      bg: '#F9D9EA', video: talkVideo, media: talkPoster, focus: '50% 42%',
      title: t('landing.card2Title'), body: t('landing.modeChatDesc'),
    },
  ];

  // Hero stat blocks: big value on top, label under it.
  const heroStats = [
    { big: t('landing.heroStat1Big'), label: t('landing.heroStat1Label') },
    { big: t('landing.heroStat2Big'), label: t('landing.heroStat2Label') },
    // Windows renders regional-indicator flag emoji as bare letters ("LT"), so the
    // flag is drawn instead of typed.
    { big: <LtFlag />, label: t('landing.heroStat3Label') },
  ];

  const steps = [
    { t: t('landing.howStep1Title'), d: t('landing.howStep1Body') },
    { t: t('landing.howStep2Title'), d: t('landing.howStep2Body') },
    { t: t('landing.howStep3Title'), d: t('landing.howStep3Body') },
  ];

  const tiles = [
    { k: '01', t: t('landing.featWordTitle'), d: t('landing.featWordDesc') },
    { k: '02', t: t('landing.featScriptsTitle'), d: t('landing.featScriptsDesc') },
    { k: '03', t: t('landing.featBreathingTitle'), d: t('landing.featBreathingDesc') },
  ];

  // FAQPage markup built from the same strings the accordion shows, so the two can
  // never drift. Google renders this route's JS, so the data is picked up client-side.
  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_KEYS.map(({ q, a }) => ({
      '@type': 'Question',
      name: t(q),
      acceptedAnswer: { '@type': 'Answer', text: t(a) },
    })),
  };

  return (
    <div className="kk">
      <KeikiStyles />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ============================= header ============================= */}
      <header className="kk-head">
        <div className="kk-head-in">
          <Link to="/" className="kk-logo" aria-label="Timo Helps">
            <img src={timoMark} alt="" /><span>Timo Helps</span>
          </Link>

          <nav className="kk-nav">
            {sections.map((s) => (
              <div className="kk-nav-item" data-opt={s.opt ? '1' : '0'} key={s.id}>
                <button type="button" className="kk-nav-link" onClick={() => goTo(s.id)}>{s.label}</button>
              </div>
            ))}
            <div
              className="kk-nav-item"
              onMouseEnter={canHover ? () => setMegaOpen(true) : undefined}
              onMouseLeave={canHover ? () => setMegaOpen(false) : undefined}
            >
              <button
                type="button"
                className={`kk-nav-link ${megaOpen ? 'kk-nav-open' : ''}`}
                aria-expanded={megaOpen}
                aria-haspopup="true"
                onClick={() => setMegaOpen((v) => !v)}
              >
                {t('footer.articles')} <span className="kk-caret">▾</span>
              </button>
              {megaOpen && (
                <div className="kk-mega">
                  {posts.map((p) => (
                    <a key={p.slug} href={`/${dir}/${p.slug}.html`}>{p.title}</a>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="kk-head-right">
            <div className="kk-nav-item" onMouseLeave={() => setLangOpen(false)}>
              <button
                type="button"
                className={`kk-nav-link ${langOpen ? 'kk-nav-open' : ''}`}
                aria-expanded={langOpen}
                aria-haspopup="true"
                aria-label="Language"
                onClick={() => setLangOpen((v) => !v)}
              >
                <span aria-hidden="true">🌐</span>
                <span className="kk-lang-label">{LANGS.find((l) => l.code === language)?.label || 'English'}</span>
                <span className="kk-caret">▾</span>
              </button>
              {langOpen && (
                <div className="kk-langmenu">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      data-on={l.code === language ? '1' : '0'}
                      onClick={() => { setLanguage(l.code); setLangOpen(false); }}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Link to="/login" className="kk-ghost">{t('landing.signIn')}</Link>
            <Link to={ctaHref} className="kk-cta kk-cta-sm kk-cta-inv" onClick={() => fireCta('nav')}>
              {t('landing.ctaTryTimo')}
            </Link>
          </div>

          <button type="button" className="kk-burger" aria-label="Menu" onClick={() => setDrawer(true)}>
            <span /><span /><span />
          </button>
        </div>
      </header>

      {drawer && (
        <div className="kk-drawer">
          <div className="kk-drawer-top">
            <span className="kk-logo"><img src={timoMark} alt="" /><span>Timo Helps</span></span>
            {/* the close glyph was a ~30px target in the corner, under the 44px minimum */}
            <button
              type="button"
              className="kk-ghost"
              aria-label="Close menu"
              style={{
                fontSize: 26,
                color: 'var(--ink)',
                minWidth: 44,
                minHeight: 44,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => setDrawer(false)}
            >
              ✕
            </button>
          </div>
          {sections.map((s) => (
            <button key={s.id} type="button" className="kk-drawer-row" onClick={() => goTo(s.id)}>{s.label}</button>
          ))}
          <a href={`/${dir}/`}>{t('footer.articles')}</a>
          <div className="kk-drawer-langs">
            {LANGS.map((l) => (
              <button
                key={l.code}
                type="button"
                data-on={l.code === language ? '1' : '0'}
                onClick={() => setLanguage(l.code)}
              >
                {l.label}
              </button>
            ))}
          </div>
          <Link to={ctaHref} className="kk-cta" style={{ width: '100%' }} onClick={() => fireCta('drawer')}>
            {t('landing.ctaTryTimo')}
          </Link>
          <Link to="/login" className="kk-ghost" style={{ display: 'block', textAlign: 'center', marginTop: 14, color: 'var(--ink)' }}>
            {t('landing.signIn')}
          </Link>
        </div>
      )}

      {/* ============================== hero ============================== */}
      <section className="kk-hero">
        <div className="kk-hero-in">
          <div className="kk-hero-copy">
            <h1 className="kk-h1">{t('landing.heroTitle')}</h1>
            <p>{t('landing.heroSubtitle')}</p>
            <div className="kk-hero-cta">
              <Link to={ctaHref} className="kk-cta kk-cta-inv" onClick={() => fireCta('hero')}>
                {t('landing.ctaGetStarted')}
              </Link>
              {/* A teacher with a code finds where it goes on the first screen,
                  not at the very bottom of the page. */}
              <button type="button" className="kk-ghost kk-hero-code" onClick={jumpToCode}>
                {t('promoCodes2.open')}
              </button>
            </div>
            <div className="kk-stats">
              {heroStats.map((s) => (
                <div key={s.label}>
                  <strong>{s.big}</strong>
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
          <img className="kk-hero-img" src={timoLanding} alt="Timo" />
        </div>
      </section>
      <Cloud from="#F2632C" to="#FFFBF7" />

      {/* ============================ trust strip ========================= */}
      <div className="kk-trust">
        <div className="kk-trust-in">
          {/* Same recording the live landing links to. Reads as a link because it is one. */}
          <a
            className="kk-radio"
            href="https://youtu.be/C2xRlzLANj0?si=QfLMDTqN0jVkSxkD"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => fireCta('radio', 'https://youtu.be/C2xRlzLANj0')}
          >
            <LtFlag />
            <span>{t('landing.radioMention')} {t('landing.radioLinkText')}</span>
          </a>
        </div>
      </div>

      {/* ============================ discover ============================ */}
      <section className="kk-sec" id="inside">
        <div className="kk-wrap">
          <h2 className="kk-h2">{t('landing.promiseTitle')}</h2>
          <p className="kk-lede">{t('landing.promiseBody')}</p>
          <div className="kk-cards">
            {cards.map((c) => (
              <article className="kk-card" key={c.title} style={{ background: c.bg }}>
                {c.video && !stillCards ? (
                  <video
                    className="kk-card-media"
                    src={c.video}
                    poster={c.media}
                    style={c.focus ? { objectPosition: c.focus } : undefined}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    aria-hidden="true"
                  />
                ) : (
                  <img className="kk-card-media" src={c.media} alt="" />
                )}
                <div className="kk-card-body">
                  <h3>{c.title}</h3>
                  <p>{c.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================== how it works ========================= */}
      <section className="kk-sec kk-how" id="how">
        <div className="kk-how-in">
          <div>
            <h2 className="kk-h2">{t('landing.howItWorks')}</h2>
            <div className="kk-steps">
              {steps.map((s, i) => (
                <div className="kk-step" key={s.t}>
                  <span className="kk-step-n">{i + 1}</span>
                  <div>
                    <h3>{s.t}</h3>
                    <p>{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to={ctaHref} className="kk-cta" onClick={() => fireCta('how')}>{t('landing.ctaGetStarted')}</Link>
          </div>
          <img className="kk-how-art" src={timoHandsUp} alt="" />
        </div>
      </section>

      {/* ============================= inside ============================= */}
      <section className="kk-sec">
        <div className="kk-wrap">
          <h2 className="kk-h2 kk-center">{t('landing.whatWeBuilt')}</h2>
          <div className="kk-grid">
            {tiles.map((tile) => (
              <article className="kk-tile" key={tile.k}>
                <b>{tile.k}</b>
                <h3>{tile.t}</h3>
                <p>{tile.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== brand band: parents love it ================ */}
      <Cloud from="#FFFBF7" to="#F2632C" />
      <section className="kk-band" id="loved">
        <div className="kk-band-in">
          <h2>{t('landing.parentsLoveIt')}</h2>
          <div className="kk-quotes">
            {[1, 2].map((n) => (
              <article className="kk-quote" key={n}>
                <p>&ldquo;{t(`landing.testimonialAlt${n}Quote`)}&rdquo;</p>
                <footer className="kk-quote-who">
                  {/* initial disc instead of a photo: these are real people who did
                      not send one, and a stock face would be a lie */}
                  <span className="kk-quote-av" aria-hidden="true">
                    {String(t(`landing.testimonialAlt${n}Name`)).trim().charAt(0)}
                  </span>
                  <span>
                    <b>{t(`landing.testimonialAlt${n}Name`)}</b>
                    <span>{t(`landing.testimonialAlt${n}Author`)}</span>
                  </span>
                </footer>
              </article>
            ))}
          </div>
        </div>
      </section>
      <Cloud from="#F2632C" to="#FFFBF7" />

      {/* ============================ articles =========================== */}
      <section className="kk-sec">
        <div className="kk-wrap">
          <div className="kk-rail-head">
            <h2 className="kk-h2" style={{ margin: 0 }}>{t('footer.articles')}</h2>
            <div className="kk-arrows">
              <button
                type="button"
                className="kk-arrow"
                style={{ opacity: railStart ? 0.4 : 1 }}
                onClick={() => glide(-1)}
                aria-label="Previous"
              >‹</button>
              <button
                type="button"
                className="kk-arrow"
                style={{ opacity: railEnd ? 0.4 : 1 }}
                onClick={() => glide(1)}
                aria-label="Next"
              >›</button>
            </div>
          </div>
          <div className="kk-rail" ref={railRef} onScroll={syncArrows}>
            {posts.map((p, i) => (
              <a className="kk-post" key={p.slug} href={`/${dir}/${p.slug}.html`}>
                <img src={covers[i % covers.length]} alt="" />
                <b>{t(`landing.artCat${i + 1}`)}</b>
                <h3>{p.title}</h3>
                <span>{t('landing.founderName')} · {postDate}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* =========================== founder / seo ======================= */}
      <section className="kk-sec kk-seo" id="story">
        <div className="kk-seo-in">
          <div className="kk-seo-art"><img src={founderImg} alt={t('landing.founderName')} /></div>
          <div className="kk-seo-body">
            <h2 className="kk-h2" style={{ marginBottom: 18 }}>{t('landing.founderName')}</h2>
            <p className="kk-pull">&ldquo;{t('landing.founderQuote')}&rdquo;</p>
            <p>{t('landing.founderStory')}</p>
            <p className="kk-sign">
              <b>{t('landing.founderName')}</b>
              <span>{t('landing.founderRole')}</span>
            </p>
          </div>
        </div>
      </section>

      {/* ===================== long form / search copy =================== */}
      <section className="kk-sec kk-long">
        <div className="kk-wrap">
          <h2 className="kk-h2">{t('landing.longTitle')}</h2>
          <div className="kk-long-body" data-open={longOpen ? '1' : '0'}>
            <p>{t('landing.longP1')}</p>
            <p>{t('landing.longP2')}</p>
            <p>{t('landing.longP3')}</p>
            <p>{t('landing.longP4')}</p>
          </div>
          <button type="button" className="kk-long-toggle" onClick={() => setLongOpen((v) => !v)}>
            {longOpen ? t('landing.readLess') : t('landing.readMore')}
          </button>
        </div>
      </section>

      {/* ============================== faq ============================== */}
      <section className="kk-sec" id="faq">
        <div className="kk-wrap">
          <h2 className="kk-h2 kk-center" style={{ marginBottom: 40 }}>{t('landing.faqSectionTitle')}</h2>
          <div className="kk-faq">
            {FAQ_KEYS.map((f, i) => {
              const open = faqOpen === i;
              return (
                <div className="kk-faq-item" key={f.q} data-open={open ? '1' : '0'}>
                  <button type="button" className="kk-faq-q" onClick={() => setFaqOpen(open ? null : i)}>
                    {t(f.q)}
                    <span className="kk-faq-sign">{open ? '−' : '+'}</span>
                  </button>
                  {open && <div className="kk-faq-a">{t(f.a)}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================ final cta ========================== */}
      <Cloud from="#FFFBF7" to="#F2632C" />
      <section className="kk-final">
        <img className="kk-final-art" src={timoHandsUp} alt="" />
        <h2>{t('landing.priceTeaserTitle')}</h2>
        <p>{t('landing.priceTeaserBody')}</p>
        <Link to={ctaHref} className="kk-cta kk-cta-inv" onClick={() => fireCta('final')}>
          {t('landing.ctaPlans')}
        </Link>
        {/* creator / event codes: same allowlist and storage key as the live landing */}
        <EventCode id="kk-code" />
      </section>

      {/* ============================= footer ============================ */}
      <footer className="kk-foot">
        <div className="kk-foot-in">
          <div>
            <span className="kk-logo"><img src={timoMark} alt="" /><span>Timo Helps</span></span>
            <p className="kk-foot-blurb">{t('landing.heroSubtitle')}</p>
            <div className="kk-foot-social">
              <a href="https://www.instagram.com/timohelps/" target="_blank" rel="noreferrer">Instagram</a>
              <a href="https://www.tiktok.com/@time2talk.eu" target="_blank" rel="noreferrer">TikTok</a>
            </div>
          </div>
          <div>
            <h4>{navLabel('landing.howItWorks')}</h4>
            <ul>
              {sections.map((s) => (
                <li key={s.id}>
                  <button type="button" className="kk-foot-row" onClick={() => goTo(s.id)}>{s.label}</button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4>{t('footer.articles')}</h4>
            <ul>
              {posts.map((p) => (
                <li key={p.slug}><a href={`/${dir}/${p.slug}.html`}>{p.title}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <h4>Timo Helps</h4>
            <ul>
              <li><Link to={ctaHref} onClick={() => fireCta('footer')}>{t('landing.ctaTryTimo')}</Link></li>
              <li><Link to="/login">{t('landing.signIn')}</Link></li>
              <li><Link to="/privacy">{t('footer.privacy')}</Link></li>
              <li><Link to="/terms">{t('footer.terms')}</Link></li>
              <li><a href="mailto:info@timetotalk.lt">{t('footer.contact')}</a></li>
            </ul>
          </div>
        </div>
        <div className="kk-foot-bottom">© {new Date().getFullYear()} Time To Talk</div>
      </footer>

      <div className="kk-sticky">
        <Link to={ctaHref} className="kk-cta" onClick={() => fireCta('sticky')}>{t('landing.ctaTryTimo')}</Link>
      </div>
    </div>
  );
};

export default LandingKeiki;
